/**
 * The text in the writing field — the imperative island.
 *
 * Preact renders nothing into it here, and that is on purpose. The field is a
 * `contenteditable`, in which browser and child own the content together:
 * caret, selection and above all the undo history belong to the browser. A
 * virtual DOM that rewrites the children breaks both — the cursor jumps, and
 * Ctrl+Z has nothing left to undo.
 *
 * Therefore everything that touches the field stands here behind a narrow
 * interface: read, write, set the caret, turn an offset into a range. The rest
 * of the app calculates only in character offsets and knows nothing of the
 * DOM.
 *
 * All functions take the field as an argument instead of fetching it
 * themselves. That is the only difference from before — and the reason why the
 * handling of offsets can now be checked without the whole app.
 */

/** A range in the text, counted in characters. */
export interface Span {
  start: number
  end: number
}

/**
 * Reading and writing text.
 *
 * A `contenteditable` has no `.value`. `textContent` is the substitute — and
 * because the field is `plaintext-only`, there really is only text in it, no
 * elements.
 */

/**
 * Reads the text together with the line breaks.
 *
 * `textContent` is NOT enough: the browser creates a <div> for every new line
 * — also with `plaintext-only` — and `textContent` concatenates their content
 * without a separator. "süß\nZweite" would become "süßZweite", and all
 * character offsets of the checking would be off.
 *
 * Therefore the nodes are walked: every <div> and every <br> is a break.
 * `rangeFor()` uses the same order, so that offsets and ranges match.
 */
export function getText(input: HTMLElement): string {
  let out = ''
  const walk = (node: Node, top: boolean): void => {
    for (const n of node.childNodes) {
      if (n.nodeType === 3) out += (n as Text).data
      else if (n.nodeName === 'BR') out += '\n'
      else {
        // A block starts a new line — unless it is the very first content.
        if (top && out !== '') out += '\n'
        walk(n, false)
      }
    }
  }
  walk(input, true)
  return out
}

/** Sets the text; breaks become true lines. */
export function setText(input: HTMLElement, t: string): void {
  input.replaceChildren()
  const lines = String(t).split('\n')
  lines.forEach((line, i) => {
    if (i > 0) input.append(document.createElement('br'))
    if (line) input.append(document.createTextNode(line))
  })
}

/**
 * The caret position as a character offset.
 *
 * A textarea gives out `selectionStart` as a number; in the `contenteditable`
 * the selection is a range over nodes. All the remaining logic — typing
 * detection, help, correction tracking — calculates in character offsets, so
 * the conversion happens here instead of a rebuild everywhere.
 *
 * @returns {{start, end}}, or null when the caret is somewhere else.
 */
export function caret(input: HTMLElement): Span | null {
  const sel = getSelection()
  if (!sel || !sel.rangeCount) return null
  const r = sel.getRangeAt(0)
  if (!input.contains(r.startContainer)) return null
  return {
    start: offsetOf(input, r.startContainer, r.startOffset),
    end: offsetOf(input, r.endContainer, r.endOffset),
  }
}

/**
 * Character offset of a point in the field.
 *
 * `Range.toString()` would be simpler, but it does not count <br> — the
 * offsets would then be off from the first new line onwards. Therefore the
 * counting runs in the same order as in `getText()`.
 */
export function offsetOf(input: HTMLElement, container: Node, offset: number): number {
  let seen = 0
  let done = false
  const visit = (node: Node, top: boolean): void => {
    for (const n of node.childNodes) {
      if (done) return
      if (n === container && n.nodeType === 3) { seen += offset; done = true; return }
      if (n.nodeType === 3) seen += (n as Text).length
      else if (n.nodeName === 'BR') seen += 1
      else {
        if (top && seen > 0) seen += 1
        if (n === container) {
          // The caret stands between the children of a block.
          let i = 0
          for (const k of n.childNodes) {
            if (i++ >= offset) break
            seen += k.nodeType === 3
              ? (k as Text).length
              : (k.nodeName === 'BR' ? 1 : (k.textContent?.length ?? 0))
          }
          done = true
          return
        }
        visit(n, false)
      }
    }
  }
  visit(input, true)
  return seen
}

/** Puts the caret on a character offset (or a selection). */
export function setCaret(input: HTMLElement, start: number, end: number = start): void {
  const r = rangeFor(input, start, end)
  if (!r) return
  const sel = getSelection()
  if (!sel) return
  sel.removeAllRanges()
  sel.addRange(r)
}

/**
 * Converts character offsets into a range over the text nodes.
 *
 * Needed for the caret and for the marks: the Highlight API works with ranges
 * as well.
 */
export function rangeFor(input: HTMLElement, start: number, end: number = start): Range {
  // Runs in the same order as getText() and counts <br> as one character —
  // otherwise the ranges would shift against the offsets of the checking from
  // the first new line onwards.
  const r = document.createRange()
  let seen = 0
  let placed = false

  const visit = (node: Node, top: boolean): boolean => {
    for (const n of node.childNodes) {
      if (n.nodeType === 3) {
        const text = n as Text
        const upTo = seen + text.length
        if (!placed && start <= upTo) {
          r.setStart(text, Math.max(0, start - seen)); placed = true
        }
        if (placed && end <= upTo) {
          r.setEnd(text, Math.max(0, end - seen)); return true
        }
        seen = upTo
      } else if (n.nodeName === 'BR') {
        seen += 1
      } else {
        if (top && seen > 0) seen += 1
        if (visit(n, false)) return true
      }
    }
    return false
  }

  if (!visit(input, true)) {
    if (!placed) { r.selectNodeContents(input); r.collapse(false) }
    else { r.setEnd(r.startContainer, r.startOffset) }
  }
  return r
}

/**
 * Inserts text without destroying the undo history.
 *
 * `execCommand` is deprecated and nevertheless right here: it is the only way
 * to keep the native undo stack. Whoever exchanges the text nodes themselves
 * instead takes Ctrl+Z away from the child — and that is the key with which
 * one helps oneself after a slip.
 */
export function insertText(input: HTMLElement, text: string): void {
  input.focus()
  document.execCommand('insertText', false, text)
}
